import React, { useState } from 'react';
import axios from 'axios';

const api = () => axios.create({
  baseURL: '/api',
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const WORKER_STEPS = [
  { icon: '✅', title: 'Complete your profile', desc: 'Add your bio, experience & availability to stand out' },
  { icon: '🔍', title: 'Browse shifts near you', desc: 'Hospitality, retail, events & more — filter by date & pay' },
  { icon: '⚡', title: 'Apply with one tap', desc: 'Hear back within hours. Get paid via secure escrow.' },
];

const EMPLOYER_STEPS = [
  { icon: '📋', title: 'Post your first shift', desc: 'Takes 2 minutes — reaches verified workers instantly' },
  { icon: '👀', title: 'Review applicants', desc: 'View profiles, ratings, and shift history at a glance' },
  { icon: '✅', title: 'Hire in seconds', desc: 'One tap to confirm — escrow handles payment automatically' },
];

export default function BetaWelcomeModal({ role, onDismiss }) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);

  const steps = role === 'employer' ? EMPLOYER_STEPS : WORKER_STEPS;

  const submitFeedback = async () => {
    setSubmitting(true);
    try {
      await api().post('/beta/feedback', { rating, message, category });
      setFeedbackDone(true);
      setTimeout(() => { dismiss(); }, 1500);
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  const dismiss = () => {
    localStorage.setItem('beta_welcomed', '1');
    onDismiss();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-br from-violet-600 to-purple-500 px-6 py-7 text-center">
          <div className="text-4xl mb-2">🚀</div>
          <h2 className="text-white text-xl font-extrabold">Welcome to ShiftMatch Beta!</h2>
          <p className="text-purple-100 text-sm mt-1">You're one of our first {role}s — let's get you started</p>
        </div>

        <div className="px-6 py-5">
          {!showFeedback ? (
            <>
              {/* Steps */}
              <div className="space-y-4 mb-6">
                {steps.map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center text-lg flex-shrink-0">{s.icon}</div>
                    <div>
                      <p className="text-sm font-bold text-neutral-900">{s.title}</p>
                      <p className="text-xs text-neutral-500 mt-0.5">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <button
                onClick={dismiss}
                className="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl text-sm transition-colors mb-3"
              >
                Let's go! →
              </button>
              <button
                onClick={() => setShowFeedback(true)}
                className="w-full py-2.5 text-xs text-neutral-400 hover:text-neutral-600 font-medium transition-colors"
              >
                💬 Got feedback? Tell us!
              </button>
            </>
          ) : feedbackDone ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">🙏</div>
              <p className="font-bold text-neutral-900">Thanks for your feedback!</p>
              <p className="text-sm text-neutral-500 mt-1">We read every single one.</p>
            </div>
          ) : (
            <>
              <h3 className="font-bold text-neutral-900 mb-4">Share your thoughts</h3>
              {/* Star rating */}
              <div className="flex gap-2 mb-4">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    onClick={() => setRating(n)}
                    className={`text-2xl transition-transform hover:scale-110 ${n <= rating ? 'opacity-100' : 'opacity-30'}`}
                  >⭐</button>
                ))}
              </div>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full border border-neutral-200 rounded-xl px-3 py-2.5 text-sm text-neutral-700 mb-3 focus:ring-2 focus:ring-violet-300 outline-none"
              >
                <option value="general">General feedback</option>
                <option value="bug">Bug report</option>
                <option value="feature">Feature request</option>
              </select>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Tell us what you think…"
                rows={3}
                className="w-full border border-neutral-200 rounded-xl px-3 py-2.5 text-sm text-neutral-700 resize-none mb-4 focus:ring-2 focus:ring-violet-300 outline-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setShowFeedback(false)}
                  className="flex-1 py-2.5 text-sm font-medium text-neutral-500 border border-neutral-200 rounded-xl hover:bg-neutral-50 transition-colors"
                >Back</button>
                <button
                  onClick={submitFeedback}
                  disabled={submitting}
                  className="flex-1 py-2.5 text-sm font-bold text-white bg-violet-600 hover:bg-violet-700 rounded-xl transition-colors disabled:opacity-50"
                >{submitting ? 'Sending…' : 'Send feedback'}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
